import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

export default function ShareButtons({ runId, level }) {
  const [challengeUrl, setChallengeUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState({
    whatsapp: 'Challenge angenommen? 😄 Teste dein IT-Wissen hier: {CHALLENGE_URL}',
    linkedin: 'Ich habe ein IT-Quiz gespielt – schaffst du mehr Punkte? {CHALLENGE_URL}'
  });

  // Load share templates from config
  useEffect(() => {
    api.get('/config/public').then(r => {
      setTemplates({
        whatsapp: r.data.whatsappShareTemplate || templates.whatsapp,
        linkedin: r.data.linkedinShareTemplate || templates.linkedin
      });
    }).catch(() => {});
  }, []);

  const ensureChallenge = async () => {
    if (challengeUrl) return challengeUrl;
    setCreating(true);
    try {
      const res = await api.post('/challenge/create', {
        challengerRunId: runId,
        displayName: 'Challenger',
        message: undefined
      });
      const url = `${window.location.origin}/challenge/${res.data.challengeId}`;
      setChallengeUrl(url);
      setCreating(false);
      return url;
    } catch {
      toast.error('Challenge konnte nicht erstellt werden');
      setCreating(false);
      return null;
    }
  };

  const formatTemplate = (template, url) => {
    let text = template
      .replace(/{CHALLENGE_URL}/g, url)
      .replace(/{LEVEL}/g, level ? (level === 'anfaenger' ? 'Anfänger' : level === 'fortgeschritten' ? 'Fortgeschritten' : 'Experte') : '');
    // Clean up double spaces from empty {LEVEL}
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  const shareWhatsApp = async () => {
    const url = await ensureChallenge();
    if (!url) return;
    const text = formatTemplate(templates.whatsapp, url);
    const encodedText = encodeURIComponent(text);
    
    // Detect mobile vs desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Mobile: use whatsapp:// protocol
      window.location.href = `whatsapp://send?text=${encodedText}`;
    } else {
      // Desktop: use web.whatsapp.com or wa.me
      window.open(`https://wa.me/?text=${encodedText}`, '_blank', 'noopener,noreferrer');
    }
  };

  const shareLinkedIn = async () => {
    const url = await ensureChallenge();
    if (!url) return;
    // LinkedIn share URL - only url parameter works reliably
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
  };

  const copyLink = async () => {
    const url = await ensureChallenge();
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast.success('Link kopiert');
  };

  return (
    <div className="space-y-2 mt-2" data-testid="share-buttons">
      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={shareWhatsApp}
          disabled={creating}
          className="h-10 sm:h-11 bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] border border-[#25D366]/30 rounded-xl text-sm font-medium"
          data-testid="share-whatsapp-btn"
        >
          <WhatsAppIcon /> <span className="ml-2">WhatsApp</span>
        </Button>
        <Button
          onClick={shareLinkedIn}
          disabled={creating}
          className="h-10 sm:h-11 bg-[#0A66C2]/15 hover:bg-[#0A66C2]/25 text-[#60A5FA] border border-[#0A66C2]/30 rounded-xl text-sm font-medium"
          data-testid="share-linkedin-btn"
        >
          <LinkedInIcon /> <span className="ml-2">LinkedIn</span>
        </Button>
      </div>
      <Button
        onClick={copyLink}
        disabled={creating}
        variant="outline"
        className="w-full h-9 sm:h-10 border-white/10 text-[#9CA3AF] hover:bg-white/5 rounded-xl text-sm"
        data-testid="share-copy-link-btn"
      >
        <Copy className="w-4 h-4 mr-2" /> {creating ? 'Erstelle Link...' : 'Link kopieren'}
      </Button>
    </div>
  );
}
